// v10/src/components/UpsellModal.js
// Roadmap B — Modal de upselling cuando el usuario intenta acceder a una feature bloqueada
import React from 'react';
import { motion } from 'framer-motion';
import { X, Lock, ArrowUpCircle } from 'lucide-react';

const PLAN_NAMES = { trial: 'Trial', basic: 'Basic', pro: 'Pro', enterprise: 'Enterprise' };

const FEATURE_PLANS = {
  pre_landing:       'pro',
  advanced_voice:    'pro',
  qr_access:        'pro',
  flight_log:       'pro',
  suppliers:        'pro',
  digital_signature: 'pro',
  icao_english:     'enterprise',
  emergency_trainer: 'enterprise',
  anac_export:      'enterprise',
};

const FEATURE_INFO = {
  pre_landing:       { emoji: '🛬', name: 'Checklist Pre-aterrizaje',  desc: 'Guía paso a paso con voz para aterrizaje seguro' },
  advanced_voice:    { emoji: '🎙️', name: 'Voz Avanzada (Valentina)', desc: 'Asistente de voz premium con ElevenLabs' },
  qr_access:        { emoji: '📱', name: 'Acceso QR por Aeronave',    desc: 'Código QR para acceder directo al checklist' },
  flight_log:       { emoji: '📒', name: 'Libro de Vuelos Digital',    desc: 'Registro de horas con export PDF y Excel' },
  suppliers:        { emoji: '🔩', name: 'Proveedores de Repuestos',   desc: 'Directorio de contactos técnicos' },
  digital_signature: { emoji: '✍️', name: 'Firma Digital',             desc: 'Firma táctil en el PDF del chequeo' },
  icao_english:     { emoji: '🌍', name: 'ICAO English',              desc: 'Módulo de inglés aeronáutico OACI' },
  emergency_trainer: { emoji: '🚨', name: 'Emergency Trainer',         desc: 'Simulador de emergencias en vuelo' },
  anac_export:      { emoji: '📋', name: 'Exportación ANAC',          desc: 'Exportación en formato oficial ANAC' },
};

const UpsellModal = ({ feature, currentPlan, onClose, darkMode }) => {
  const info = FEATURE_INFO[feature] || { emoji: '🔒', name: feature, desc: '' };
  const requiredPlan = FEATURE_PLANS[feature] || 'pro';
  const requiredPlanName = PLAN_NAMES[requiredPlan] || requiredPlan;

  const bgColor = darkMode ? '#1e293b' : 'white';
  const textColor = darkMode ? 'white' : '#0f172a';
  const subColor = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 140, damping: 18 }}
        style={{ width: '100%', maxWidth: 380, borderRadius: 28, overflow: 'hidden', background: bgColor, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}
        onClick={e => e.stopPropagation()}>

        {/* Header gradient */}
        <div style={{ padding: '28px 24px 20px', background: 'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(37,99,235,0.08))', textAlign: 'center', position: 'relative' }}>
          <button onClick={onClose}
            style={{ position: 'absolute', top: 12, right: 12, background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: subColor }}>
            <X style={{ width: 16, height: 16 }} />
          </button>

          <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg,#7c3aed,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 30 }}>
            {info.emoji}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
            <Lock style={{ width: 14, height: 14, color: '#f59e0b' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>Funcionalidad bloqueada</span>
          </div>

          <h3 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 900, color: textColor }}>{info.name}</h3>
          <p style={{ margin: 0, fontSize: 13, color: subColor, lineHeight: 1.5 }}>{info.desc}</p>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px 24px' }}>
          <div style={{ background: darkMode ? 'rgba(124,58,237,0.08)' : 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 16, padding: '16px 18px', marginBottom: 20 }}>
            <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>
              <ArrowUpCircle style={{ width: 14, height: 14, display: 'inline', verticalAlign: -2, marginRight: 6 }} />
              Disponible en el plan {requiredPlanName}
            </p>
            <p style={{ margin: 0, fontSize: 12, color: subColor, lineHeight: 1.5 }}>
              Tu aeroclub usa el plan <strong>{PLAN_NAMES[currentPlan] || currentPlan}</strong>. Contactá al administrador para más información sobre la actualización.
            </p>
          </div>

          <button onClick={onClose}
            style={{ width: '100%', padding: 14, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 15, color: 'white', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 6px 20px rgba(124,58,237,0.3)' }}>
            Entendido
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UpsellModal;
